<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Input, Slider, Toggle } from '@Shelly/shelly-design-system';
	//Types
	import type { LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
	import { capFirstChar, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';

	export let controlOption: LocalControlOptions,
		minBrightness = 0,
		transitionDuration: {
			min: number;
			max: number;
			showError: boolean;
			disabled: boolean;
		},
		showOffsetProperty = true;

	if (controlOption.params.brightness == 0) {
		controlOption.params.brightness = minBrightness;
	}
	$: error = getErrorOrInfoMessageForMinAndMax(
		controlOption.params.transition_duration,
		transitionDuration.min,
		transitionDuration.max,
	).error;

	const a11yProps = {
		sliderAriaLabel: $format('slider_set_brightness', {
			default: 'Set brightness',
		}),
		ariaLabel: $format('slider_set_brightness', { default: 'Set brightness' }),
		ariaLabelValue: $format('slider_set_brightness', {
			default: 'Set brightness',
		}),
	};

	const a11yPropsOffset = {
		sliderAriaLabel: $format('label_set_offset_property', {
			default: 'Set White offset property',
		}),
		ariaLabel: $format('label_set_offset_property', {
			default: 'Set White offset property',
		}),
		ariaLabelValue: $format('label_set_offset_property', {
			default: 'Set White offset property',
		}),
	};
</script>

<div>
	<Toggle class="mb-xs mt-s" size="m" bind:checked={controlOption.attr.brightness} id="toggle-2a35dc8">
		{capFirstChar(
			$format('label_set_brightness_property', {
				default: 'set brightness property',
			}),
		)}
	</Toggle>

	<Text styleType="body-small" class="mb-s {controlOption.attr.brightness ? '' : 'text-color-3'}">
		{$format('min_max_unit_range', {
			default: '(Minimum: {min}, Maximum: {max})',
			values: { min: minBrightness, max: 100 },
		})}
	</Text>
	<Slider
		id="slider-gvi0ndu"
		{a11yProps}
		type="simple"
		class="mb-s"
		value={controlOption.params.brightness}
		disabled={!controlOption.attr.brightness}
		min={minBrightness}
		unit="%"
		on:value_change={(e) => {
			controlOption.params.brightness = e.detail.value;
		}}
		title={capFirstChar(
			$format('label_light_slider_title_selected', {
				values: {
					brightness: controlOption.params.brightness,
				},
				default: `Selected:`,
			}),
		)}
	/>

	{#if controlOption.attr?.transition_duration != undefined && !transitionDuration.disabled}
		<Toggle
			class="mb-xs"
			size="m"
			bind:checked={controlOption.attr.transition_duration}
			id="toggle-n557pq1"
		>
			{capFirstChar(
				$format('label_set_transition_duration_property', {
					default: 'set transition duration property',
				}),
			)}
		</Toggle>

		<Text
			styleType="body-small"
			class="mb-s {controlOption.attr.transition_duration ? '' : 'text-color-3'}"
		>
			{$format('min_max_unit_range', {
				values: transitionDuration,
				default: '(Minimum: {min}, Maximum: {max})',
			})}
		</Text>
		<Input
			class="mb-m"
			value={controlOption.params.transition_duration?.toString() || ''}
			label={$format('label_placeholder_transition_duration', {
				default: 'e.g. 10 (in 10 seconds)',
			})}
			disabled={!controlOption.attr.transition_duration}
			type="number"
			size="m"
			min={transitionDuration.min}
			max={transitionDuration.max}
			tabIndex="-1"
			{error}
			on:input={(e) => {
				controlOption.params.transition_duration = e.target['value'];
			}}
			id="input-vs8s1ho"
			aria-labelledby="input-vs8s1ho"
		/>
	{/if}

	{#if showOffsetProperty && controlOption.attr?.offset != undefined}
		<Toggle
			class="mb-xs"
			size="m"
			bind:checked={controlOption.attr.offset}
			id="toggle-offset-property"
		>
			{capFirstChar(
				$format('label_set_offset_property', {
					default: 'Set Offset property',
				}),
			)}
		</Toggle>

		<Text styleType="body-small" class="mb-s {controlOption.attr.offset ? '' : 'text-color-3'}">
			{$format('label_set_offset_property_desc', {
				default: 'This property cannot be used together with brightness!',
			})}
		</Text>
		<Slider
			id="slider-offset-property"
			class="mb-s"
			a11yProps={a11yPropsOffset}
			type="simple"
			value={controlOption.params.offset}
			disabled={!controlOption.attr.offset}
			min={-100}
			max={100}
			unit="%"
			on:value_change={(e) => {
				controlOption.params.offset = e.detail.value;
			}}
			title={capFirstChar(
				$format('label_light_slider_title_selected', {
					values: {
						brightness: controlOption.params.offset,
					},
					default: `Selected:`,
				}),
			)}
		/>
	{/if}

	{#if showOffsetProperty && controlOption.attr?.offset_white != undefined}
		<Toggle
			class="mb-xs"
			size="m"
			bind:checked={controlOption.attr.offset_white}
			id="toggle-offset-white"
		>
			{capFirstChar(
				$format('label_set_offset_white_property', {
					default: 'Set White offset property',
				}),
			)}
		</Toggle>

		<Text
			styleType="body-small"
			class="mb-s {controlOption.attr.offset_white ? '' : 'text-color-3'}"
		>
			{$format('label_set_offset_white_property_desc', {
				default: 'This property cannot be used together with white!',
			})}
		</Text>
		<Slider
			id="slider-offset-white"
			class="mb-s"
			a11yProps={a11yPropsOffset}
			type="simple"
			value={controlOption.params.offset_white}
			disabled={!controlOption.attr.offset_white}
			min={-100}
			max={100}
			unit="%"
			on:value_change={(e) => {
				controlOption.params.offset_white = e.detail.value;
			}}
			title={capFirstChar(
				$format('label_light_slider_title_selected', {
					values: {
						brightness: controlOption.params.offset_white,
					},
					default: `Selected:`,
				}),
			)}
		/>
	{/if}
</div>
